import React, { useEffect, useState } from "react";
import { getLivePublicMatches } from "../store/supabaseMatches";
import PublicLayout from "../components/public/PublicLayout";
import { MatchCard, MatchCardSkeleton, type PublicMatch } from "../components/public/MatchCard";

function isAbortError(err: unknown): boolean {
  return err instanceof Error && (err.name === "AbortError" || err.message === "signal is aborted without reason");
}

export default function LiveMatchesPage() {
  const [matches, setMatches] = useState<PublicMatch[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;

    const fetchLive = async () => {
      try {
        const data = await getLivePublicMatches();
        if (!mounted) return;
        setMatches((data || []).filter((m: PublicMatch) => m.status === "live"));
        setLoading(false);
      } catch (err) {
        if (!mounted) return;
        if (isAbortError(err)) {
          console.log("[LiveMatchesPage] Fetch aborted on unmount");
          return;
        }
        console.error("[LiveMatchesPage] Error fetching live matches:", err);
        setLoading(false);
      }
    };

    fetchLive();

    const interval = setInterval(async () => {
      if (!mounted) return;
      try {
        const data = await getLivePublicMatches();
        if (!mounted) return;
        setMatches((data || []).filter((m: PublicMatch) => m.status === "live"));
      } catch (err) {
        if (!mounted) return;
        if (isAbortError(err)) {
          console.log("[LiveMatchesPage] Poll fetch aborted");
          return;
        }
        console.error("[LiveMatchesPage] Error polling live matches:", err);
      }
    }, 30000);

    return () => {
      mounted = false;
      clearInterval(interval);
    };
  }, []);

  return (
    <PublicLayout activePath="/live" showBottomNav>
      <div className="max-w-6xl mx-auto px-4 pt-8">
        <div className="section-panel-live rounded-2xl p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse shadow-lg shadow-red-500/50" />
            <h1 className="text-2xl font-black text-white uppercase tracking-wide">Live Matches</h1>
            {!loading && matches.length > 0 && (
              <span className="ml-auto bg-red-500/20 text-red-400 text-xs font-bold px-2.5 py-1 rounded-full border border-red-500/30">
                {matches.length} LIVE
              </span>
            )}
          </div>

          {loading ? (
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
              <MatchCardSkeleton />
              <MatchCardSkeleton />
              <MatchCardSkeleton />
            </div>
          ) : matches.length > 0 ? (
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
              {matches.map((match) => (
                <MatchCard
                  key={match.id}
                  match={match}
                  onClick={() => { window.location.href = `/match/${match.id}`; }}
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <div className="text-7xl mb-4">🏏</div>
              <p className="text-slate-300 text-xl font-semibold mb-2">No live matches right now</p>
              <p className="text-slate-400/70 text-sm mb-6">Check back soon or browse upcoming fixtures</p>
              <button
                onClick={() => { window.location.href = "/fixtures"; }}
                className="bg-emerald-600/80 hover:bg-emerald-600 text-white px-6 py-3 rounded-lg font-semibold text-sm transition-all"
              >
                View Fixtures
              </button>
            </div>
          )}
        </div>
      </div>
    </PublicLayout>
  );
}
